import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChartLineUp, Newspaper, ArrowsClockwise } from "@phosphor-icons/react";
import { fetchInsights } from "../lib/api";
import { relativeTime } from "../lib/time";
import SEO from "../components/SEO";

const tilt = (s) => (s >= 60 ? "bullish" : s <= 40 ? "bearish" : "neutral");
const tiltColor = (s) =>
  s == null ? "#A1A1AA" : s >= 60 ? "#34C759" : s <= 40 ? "#FF3B30" : "#FFCC00";

function PostCard({ post, onClick }) {
  const t = tilt(post.sentiment_score);
  const c = tiltColor(post.sentiment_score);
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-surface border border-white/10 hover:border-white/30 p-5 flex flex-col gap-3 transition-colors duration-150"
      data-testid={`insight-card-${post.slug}`}
    >
      <div className="flex items-center gap-2">
        <span className="w-2 h-2" style={{ background: c }} />
        <span className="mono text-[10px] uppercase tracking-[0.14em]" style={{ color: c }}>
          {post.region} · {t}
        </span>
        <span className="overline ml-auto">{relativeTime(post.created_at || post.date)}</span>
      </div>
      <h3 className="heading text-lg font-semibold tracking-tight leading-snug">{post.title}</h3>
      {post.tldr && (
        <p className="text-sm text-white/70 leading-relaxed line-clamp-3">{post.tldr}</p>
      )}
      <div className="flex items-center justify-between pt-2 border-t border-white/5 mt-auto">
        <div className="flex flex-wrap gap-1">
          {(post.tags || []).slice(0, 3).map((tag) => (
            <span key={tag} className="mono text-[10px] text-white/55 border border-white/10 px-1.5 py-0.5">
              {tag}
            </span>
          ))}
        </div>
        {post.sentiment_score != null && (
          <span className="mono text-xs font-bold" style={{ color: c }}>
            {Math.round(post.sentiment_score)}/100
          </span>
        )}
      </div>
    </button>
  );
}

export default function InsightsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = (warm = false) => {
    if (warm) setRefreshing(true);
    fetchInsights(warm)
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]))
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  };

  useEffect(() => {
    // First load triggers warm so today's posts populate if missing.
    load(true);
    // Re-poll a few times in case background generation is still finishing.
    const ids = [
      setTimeout(() => load(false), 8000),
      setTimeout(() => load(false), 20000),
      setTimeout(() => load(false), 40000),
    ];
    return () => ids.forEach(clearTimeout);
  }, []);

  // Sort newest first; group by date
  const groups = items.reduce((acc, p) => {
    const k = p.date || p.created_at?.slice(0, 10) || "—";
    if (!acc[k]) acc[k] = [];
    acc[k].push(p);
    return acc;
  }, {});
  const dates = Object.keys(groups).sort().reverse();

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="insights-page">
      <SEO
        title="Daily AI Market Insights"
        path="/insights"
        description="Daily AI-written market briefings for Global, USA, India, Europe, Asia and Luxury Watches. Sharp, neutral, 250-word briefings grounded in live headlines — generated fresh every day."
        keywords={["market briefing", "daily market insights", "AI market analysis", "stock market today", "Sensex outlook", "S&P 500 today", "watch market briefing"]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Sent-i-meter Insights",
          url: "https://sent-i-meter.com/insights",
          description: "Daily AI-generated market briefings across global regions and the luxury watch segment.",
        }}
      />

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10 px-4 md:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white" data-testid="insights-back">
            <ArrowLeft size={16} weight="bold" />
            <span className="overline">back</span>
          </button>
          <div className="w-px h-6 bg-white/10 mx-2" />
          <button onClick={() => navigate("/")} className="w-8 h-8 bg-white text-black flex items-center justify-center">
            <ChartLineUp size={16} weight="bold" />
          </button>
          <div className="hidden sm:block">
            <div className="heading text-sm font-bold tracking-tight leading-none">INSIGHTS</div>
            <div className="overline mt-0.5 leading-none">daily ai market briefings</div>
          </div>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-2.5 py-1.5 mono text-[10px] uppercase tracking-[0.16em] border border-white/10 hover:border-white/40 text-white/70 hover:text-white transition-colors duration-150 disabled:opacity-40"
          data-testid="insights-refresh"
        >
          <ArrowsClockwise size={12} weight="bold" className={refreshing ? "animate-spin" : ""} />
          <span className="hidden md:inline">Refresh</span>
        </button>
      </header>

      <main className="px-4 md:px-6 py-6 max-w-[1200px] mx-auto">
        <section className="bg-surface border border-white/10 p-5 mb-6 flex items-start gap-4" data-testid="insights-hero">
          <div className="w-10 h-10 bg-white text-black flex items-center justify-center shrink-0">
            <Newspaper size={20} weight="duotone" />
          </div>
          <div>
            <div className="overline">ai market journal · claude sonnet 4.5</div>
            <h1 className="heading text-2xl font-bold tracking-tight mt-1">Daily Market Briefings</h1>
            <p className="text-sm text-white/70 mt-2 max-w-2xl leading-relaxed">
              A fresh ~250-word briefing for each region, every day. Grounded in live headlines from
              CNBC, Reuters, MarketWatch, Economic Times, Hodinkee and more. Sharp, neutral, no price predictions.
            </p>
          </div>
        </section>

        {loading && (
          <div className="overline py-12 text-center">loading briefings…</div>
        )}

        {!loading && items.length === 0 && (
          <div className="bg-surface border border-white/10 p-8 text-center" data-testid="insights-empty">
            <div className="overline mb-2">no briefings yet</div>
            <p className="text-sm text-white/70">
              Try clicking refresh — today's posts are generated on first request.
            </p>
          </div>
        )}

        {dates.map((d) => (
          <section key={d} className="mb-8" data-testid={`insights-date-${d}`}>
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="heading text-base font-semibold tracking-tight">
                {new Date(d).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </h2>
              <span className="overline">{groups[d].length} briefings</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {groups[d].map((p) => (
                <PostCard key={p.slug} post={p} onClick={() => navigate(`/insights/${encodeURIComponent(p.slug)}`)} />
              ))}
            </div>
          </section>
        ))}

        <footer className="border-t border-white/10 pt-4 mt-8 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="overline hover:text-white" data-testid="insights-footer-back">
            ← back to dashboard
          </button>
          <span className="overline">ai-written · grounded in live news · neutral analyst tone</span>
        </footer>
      </main>
    </div>
  );
}
